import { useEffect } from 'react';
import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

type ServerFieldError = { field: string; message: string };

/**
 * Maps server-side validation errors onto react-hook-form fields.
 *
 * `fieldMap` maps a server error `field` key to the corresponding form field
 * path (typically the `form2` table from `createServerFieldMappings`). Errors
 * whose `field` is not present in the map are ignored. No-ops while either
 * `serverErrors` or `fieldMap` is undefined.
 */
export const useServerErrors = <T extends FieldValues>(
  serverErrors: ServerFieldError[] | undefined,
  fieldMap: Record<string, string> | undefined,
  setError: UseFormSetError<T>,
): void => {
  useEffect(() => {
    if (!serverErrors || !fieldMap) {
      return;
    }
    serverErrors.forEach((error) => {
      if (Object.keys(fieldMap).includes(error.field)) {
        setError(fieldMap[error.field] as FieldPath<T>, { type: 'server', message: error.message });
      }
    });
  }, [serverErrors, fieldMap, setError]);
};
